import React, { useState } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { User } from 'lucide-react';
import { MessageSquare } from 'lucide-react';
import { MessageActionMenu } from './MessageActionMenu';
import { MessageReactions } from './MessageReactions';
import { ReactionPicker } from './ReactionPicker';
import type { Activity, MessageReactions as MessageReactionsType } from '../../../types/activities';
import { cn } from '../../../lib/utils';
import { EmptyState } from '../../ui/EmptyState';
import { useResolveActivityUserNames, getActivityUserDisplayName } from '../../../hooks/useResolveActivityUserNames';

interface MessageListProps {
  messages: Activity[];
  currentUserId: string | undefined;
  onEdit: (messageId: string, newText: string) => Promise<void>;
  onDelete: (messageId: string) => Promise<void>;
  onReaction: (messageId: string, reaction: 'thumbs_up' | 'thumbs_down' | 'question' | 'laugh') => Promise<void>;
}

export function MessageList({
  messages,
  currentUserId,
  onEdit,
  onDelete,
  onReaction,
}: MessageListProps) {
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [openMenuMessageId, setOpenMenuMessageId] = useState<string | null>(null);
  
  // Batch-resolve user names for messages that need it
  const resolvedUserNames = useResolveActivityUserNames(messages);

  if (messages.length === 0) {
    return (
      <EmptyState
        size="compact"
        variant="ghost"
        icon={<MessageSquare className="h-5 w-5" aria-hidden="true" />}
        description="No messages yet. Start the conversation!"
      />
    );
  }

  return (
    <div className="p-4 space-y-4">
      {messages.map((message: Activity) => {
        const isFromCurrentUser = message.user_id === currentUserId;
        const isHovered = hoveredMessageId === message.id;
        const reactions = (message.metadata?.reactions as MessageReactionsType) || {};

        const isMenuOpen = openMenuMessageId === message.id;
        const shouldShowMenu = isHovered || isMenuOpen;

        return (
          <div
            key={message.id}
            className={cn('flex gap-3 group', isFromCurrentUser ? 'justify-end' : 'justify-start')}
            onMouseEnter={() => setHoveredMessageId(message.id)}
            onMouseLeave={(e) => {
              // Don't clear hover if the dropdown menu is open and mouse is moving into it
              const target = e.relatedTarget;
              if (isMenuOpen && target && target instanceof HTMLElement && target.closest('[role="menu"]')) {
                return;
              }
              setHoveredMessageId(null);
            }}
          >
            <div
              className={cn(
                'relative max-w-[80%] rounded-lg px-4 py-2',
                isFromCurrentUser ? 'bg-primary text-primary-foreground' : 'bg-muted'
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  {!isFromCurrentUser && (
                    <div className="flex items-center gap-2 mb-1">
                      <User className="h-3 w-3" />
                      <p className="text-xs font-medium">{getActivityUserDisplayName(message, resolvedUserNames)}</p>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message.text || message.message}</p>
                  <p
                    className={cn(
                      'text-xs mt-1',
                      isFromCurrentUser ? 'text-primary-foreground/70' : 'text-muted-foreground'
                    )}
                  >
                    {formatDistanceToNowStrict(new Date(message.timestamp), { addSuffix: true })}
                  </p>
                  {Object.keys(reactions).length > 0 && (
                    <MessageReactions
                      reactions={reactions}
                      currentUserId={currentUserId}
                      onReaction={(reaction) => onReaction(message.id, reaction)}
                      message={message}
                    />
                  )}
                </div>
                {shouldShowMenu && (
                  <div className={cn('flex items-start gap-1', isFromCurrentUser ? 'ml-2' : 'mr-2')}>
                    {isFromCurrentUser ? (
                      <MessageActionMenu
                        messageId={message.id}
                        messageText={message.text || message.message || ''}
                        canEdit={true}
                        canDelete={true}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onOpenChange={(open) => {
                          setOpenMenuMessageId(open ? message.id : null);
                        }}
                      />
                    ) : (
                      <ReactionPicker
                        currentUserId={currentUserId}
                        existingReactions={reactions}
                        onReaction={(reaction) => onReaction(message.id, reaction)}
                        message={message}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
